import { Engine } from "@carsdotcom/online-shopper-fea";
import { GoogleAnalyticsConfig } from "@carsdotcom/online-shopper-fea/lib/providers/GoogleAnalyticsProvider/types";
import { GTMPageViewEvent, HyundaiEvent } from "@carsdotcom/online-shopper-fea/lib/providers/HyundaiProvider/types";
import { NissanEvent } from "@carsdotcom/online-shopper-fea/lib/providers/NissanProvider/types";
import { OnlineShopperConfig } from "@carsdotcom/online-shopper-fea/lib/providers/OnlineShopper/types";
import { DigitalDataEvent } from "@carsdotcom/online-shopper-fea/lib/shared/types";
import { getAppState } from "src/store";
import { BingEventData } from "@carsdotcom/online-shopper-fea/lib/providers/BingProvider/types";
import { sendGoogleAnalyticsEvent } from "src/utils/ga";
import { sendBingEvent } from "src/utils/bing-ads";
import { sendFbEvent } from "src/utils/fbPixel";
import { isLockedPricingSetup } from "src/store/selectors/account";

export class AnalyticsFEA {
    private _isInitialized: boolean;
    private _engine: Engine;
    private analyticsConfigs: any[];
    private queue: any[];

    constructor() {
        this._engine = new Engine({
            taggers: [],
        });
    }

    public isInitialized = () => this._isInitialized;

    public initialize() {
        const appState = getAppState();

        if (!appState || this._isInitialized) {
            return;
        }

        const { deal, onlineShopper, account, tracking } = appState;
        const uuid = (deal && deal.uuid) || "";
        const env = (onlineShopper && onlineShopper.env) || "dev";
        this.analyticsConfigs = (account && account.front_end_analytics_config) || [];

        if (process.env.NODE_ENV === "development") {
            this.analyticsConfigs.push({
                name: "console",
            });
        }

        this.analyticsConfigs.push({
            name: "os",
            payload: {
                env,
                uuid,
            } as OnlineShopperConfig,
        });

        if (
            account.integration_settings?.Analytics?.google_analytics &&
            account.integration_settings.Analytics.google_analytics.length > 0
        ) {
            this.analyticsConfigs.push({
                name: "ga",
                payload: {
                    trackers: account.integration_settings.Analytics.google_analytics,
                    clientId: tracking.clientId,
                } as GoogleAnalyticsConfig,
            });
        }

        if (account.integration_settings?.["AutoNation Tagging"]) {
            const autoNationConfigIndex = this.analyticsConfigs.findIndex(
                (config: any) => config.name === "autonation"
            );
            this.analyticsConfigs[autoNationConfigIndex].payload = { pageType: onlineShopper.pageType };
        }

        this.setEventCallbackForTag("fca", this.publishFCAEvent);

        this.setEventCallbackForTag("hyundai", this.publishHyundaiEvent);

        this.setEventCallbackForTag("nissan", this.publishNissanEvent);

        this.setEventCallbackForTag("autonation", this.publishAutoNationEvent);

        this.setEventCallbackForTag("ga", this.publishGAEvent);

        this.setEventCallbackForTag("fp", this.publishFBEvent);

        this.setEventCallbackForTag("bing", this.publishBingEvent);

        this._engine = new Engine({
            taggers: this.analyticsConfigs,
            isLockedPricing: isLockedPricingSetup(appState) && appState.onlineShopper.isAuthed,
        });

        this._isInitialized = true;
    }

    get engine() {
        return this._engine;
    }

    public updateOSProvider(uuid: string) {
        const appState = getAppState();
        const env = (appState && appState.onlineShopper && appState.onlineShopper.env) || "dev";

        this._engine.updateProvider({
            name: "os",
            payload: {
                env,
                uuid,
            } as OnlineShopperConfig,
        });
    }

    private publishFCAEvent = (event: DigitalDataEvent) => {
        const digitalData = (window as any).parent.digitalData;

        if (digitalData && digitalData.newEvent) {
            digitalData.newEvent(event);
        }
    };

    private publishHyundaiEvent = (event: HyundaiEvent | GTMPageViewEvent) => {
        const HYUNDAI_PROVIDER_ID = "Dealer Inspire";
        const dataLayer = (window as any).parent.dataLayer;
        if (!dataLayer) return;
        // ONSHOP-7117: If the website has a meta tag indicating it's operated by a different provider, make sure Online Shopper's events are correctly attributed
        const providerMetaTag = document.querySelector("meta[name='providerID']");
        if (providerMetaTag && providerMetaTag.getAttribute("content") !== HYUNDAI_PROVIDER_ID) {
            event["hyundai-trilogy.ProviderID"] = HYUNDAI_PROVIDER_ID;
        }
        dataLayer.push(event);
    };

    private publishNissanEvent = (event: NissanEvent) => {
        const dataLayer = (window as any).parent.dataLayer;

        if (dataLayer) {
            dataLayer.push(event);
        }
    };

    private publishAutoNationEvent = (event: any) => {
        const dataLayer = (window as any).parent.utag;

        if (dataLayer) {
            if (event.hasOwnProperty("lead_hyperion") && window.utag_data?.hyperion_id) {
                event.lead_hyperion = window.utag_data.hyperion_id;
            }

            if (event.hasOwnProperty("lead_store_name") && window.utag_data?.store_name) {
                event.lead_store_name = window.utag_data.store_name;
            }

            dataLayer.link(event);
        }
    };

    private publishGAEvent = (event: any, config: any) =>
        config.trackers.forEach((tid: string, index: number) => {
            sendGoogleAnalyticsEvent(tid, index, event);
        });

    private publishBingEvent = (bingTagID: string, event: BingEventData) => sendBingEvent(bingTagID, event);

    private publishFBEvent = (pixelId: string, eventArgs: [string, string, object]) => sendFbEvent(pixelId, eventArgs);

    private setEventCallbackForTag = (OEMTagName: string, eventCallback: (...args: any) => void) => {
        const OEMConfigIndex = this.analyticsConfigs.findIndex((a: any) => a.name === OEMTagName);
        if (OEMConfigIndex !== -1) {
            // Overwriting the existing OEM config with ours, which adds a callback function to send events outside the iframe.
            this.analyticsConfigs[OEMConfigIndex] = {
                name: OEMTagName,
                payload: {
                    ...this.analyticsConfigs[OEMConfigIndex].payload,
                    eventCallback,
                },
            };
        }
    };
}

export const analyticsFEA = new AnalyticsFEA();
